import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Stack,
  Avatar,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import HistoryIcon from '@mui/icons-material/History';
import { CloseRounded } from '@mui/icons-material';
import TicketForm from './TicketForm';
import TicketHistory from './TicketHistory';

const PRIMARY = '#1570EF';
const PRIMARY_DARK = '#0E5BC4';


const CrmHelpDesk = () => {
  const [view, setView] = useState('home'); // 'home' | 'history'
  const [createOpen, setCreateOpen] = useState(false);

  const handleCreateClose = () => setCreateOpen(false);

  if (view === 'history') {
    return <TicketHistory onBack={() => setView('home')} />;
  }

  const ActionCard = ({ icon, title, subtitle, onClick }) => (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        position: 'relative',
        borderRadius: 3,
        overflow: 'hidden',
        cursor: 'pointer',
        backgroundColor: '#fff',
        boxShadow: '0 6px 20px rgba(16,24,40,0.06)',
        transition: 'all 0.25s ease',
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: '0 16px 32px rgba(21,112,239,0.18)',
        },
      }}>
      {/* top accent bar */}
      <Box sx={{ height: 6, backgroundColor: PRIMARY }} />
      <Box sx={{ p: { xs: 4, md: 6 }, textAlign: 'center' }}>
        <Avatar
          sx={{
            width: 80,
            height: 80,
            bgcolor: PRIMARY,
            color: '#fff',
            mx: 'auto',
            mb: 3,
            boxShadow: '0 8px 18px rgba(21,112,239,0.35)',
          }}>
          {icon}
        </Avatar>
        <Typography
          sx={{ fontFamily: 'inter-semibold', fontWeight: 700, fontSize: '2.2rem', color: '#101828', mb: 1 }}>
          {title}
        </Typography>
        <Typography sx={{ fontFamily: 'Roboto-Regular', fontSize: '1.4rem', color: '#667085' }}>
          {subtitle}
        </Typography>
      </Box>
    </Paper>
  );

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', backgroundColor: '#eef0f3', py: 4 }}>
      <Container maxWidth="lg">
        {/* Hero banner */}
        <Paper
          elevation={0}
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 3,
            p: { xs: 3, md: 4 },
            mb: 4,
            background: `linear-gradient(90deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
            color: '#fff',
          }}>
          {/* decorative circle */}
          <Box
            sx={{
              position: 'absolute',
              right: -40,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 220,
              height: 220,
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.08)',
            }}
          />
          <Stack direction="row" spacing={3} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
            <Avatar sx={{ width: 64, height: 64, bgcolor: 'rgba(255,255,255,0.18)', color: '#fff' }}>
              <SupportAgentIcon sx={{ fontSize: 34 }} />
            </Avatar>
            <Box>
              <Typography sx={{ fontFamily: 'inter-semibold', fontWeight: 800, fontSize: { xs: '2.4rem', md: '3.2rem' }, lineHeight: 1.1 }}>
                CRM Help Desk
              </Typography>
              <Typography sx={{ fontFamily: 'Roboto-Regular', fontSize: { xs: '1.4rem', md: '1.7rem' }, opacity: 0.95, mt: 0.5 }}>
                Your one-stop solution for technical support
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Action cards */}
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <ActionCard
              icon={<AddCircleIcon sx={{ fontSize: 40 }} />}
              title="Create New Ticket"
              subtitle="Submit a new support request"
              onClick={() => setCreateOpen(true)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ActionCard
              icon={<HistoryIcon sx={{ fontSize: 40 }} />}
              title="View Ticket History"
              subtitle="Check your previous tickets"
              onClick={() => setView('history')}
            />
          </Grid>
        </Grid>

        {/* Need Help */}
        <Paper
          elevation={0}
          sx={{
            mt: 4,
            borderRadius: 3,
            p: { xs: 3, md: 5 },
            textAlign: 'center',
            backgroundColor: '#fff',
            boxShadow: '0 6px 20px rgba(16,24,40,0.06)',
          }}>
          <Typography sx={{ fontFamily: 'inter-semibold', fontWeight: 700, fontSize: '2rem', color: '#101828', mb: 1.5 }}>
            Need Help?
          </Typography>
          <Typography sx={{ fontFamily: 'Roboto-Regular', fontSize: '1.5rem', color: '#667085', maxWidth: 680, mx: 'auto' }}>
            Our support team is here to help you with any technical issues or questions. Create a new ticket or check the
            status of your existing requests.
          </Typography>
        </Paper>
      </Container>

      {/* Create ticket modal */}
      <Dialog open={createOpen} onClose={handleCreateClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle
          sx={{
            background: PRIMARY,
            color: '#fff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontFamily: 'inter-semibold',
          }}>
          Create New Ticket
          <IconButton onClick={handleCreateClose} sx={{ color: '#fff' }}>
            <CloseRounded />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <TicketForm onClose={handleCreateClose} />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CrmHelpDesk;
